import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { TranslateProvider } from 'autobiz-translate';
import { getUserSelector } from '../../view-models-generators/userSelector';
import { setTokenUseCase } from '../../../../hexagon/usecases/setToken/setTokenUseCase';
import { getUserInfoUseCase } from '../../../../hexagon/usecases/getUserInfo/getUserInfoUseCase';
import { userFeedsRefreshDelayInSeconds } from '../../../../hexagon/config/appConfig';
import { getUserFeedsUseCase } from '../../../../hexagon/usecases/getUserFeeds/getUserFeedsUseCase';
import { getUserStatsUseCase } from '../../../../hexagon/usecases/getUserStats/getUserStatsUseCase';
import ErrorPage from '../Error/Error';
import HelmetComponent from '../HelmetComponent/HelmetComponent';
import AuthSpinner from '../AuthSpinner/AuthSpinner';
import { ACCOUNTS_URL_BASE } from '../../../../config';
import { AuthState } from '../../state/authTokenActions';
import { authReducer, initialState } from '../../state/authTokenReducer';
import {
    clearAuthTokenCookie,
    getAuthTokenFromCookie,
    setAuthTokenCookie,
} from '../../../../utils/authCookie';
import { rewriteRedirectUri } from '../../../../utils/preserveRawParams';
import { clearSurveyCookie } from '../../../../utils/surveyCookie';

export async function handleSharedLinkLogin(token: any) {
    const res = await axios.get(`${ACCOUNTS_URL_BASE}/me`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    if (res.status !== 200) throw new Error('Auth failed');
    return res.data;
}

interface AuthTokenContextProps extends AuthState {
    verifyToken: (options?: { storageKey?: string }) => void;
    logout: (options?: { storageKey?: string }) => void;
    redirectUri: string;
}

// Crée le contexte avec des valeurs par défaut
const HybridAuthContext = createContext<AuthTokenContextProps>({
    ...initialState,
    verifyToken: async () => {},
    logout: async () => {},
    redirectUri: '',
});

export function useHybridAuth() {
    const context = useContext(HybridAuthContext);
    if (!context) {
        throw new Error('useHybridAuth must be used within a HybridAuthProvider.');
    }
    return context;
}

export const HybridAuthProvider = ({ children }: any) => {
    const { tokenStatus, user } = useSelector(getUserSelector);
    const dispatch = useDispatch();
    const { getIdTokenClaims, isAuthenticated, error: auth0Error } = useAuth0();
    const [time, setTime] = useState(Date.now());
    const [state, dispatchAuth] = useReducer(authReducer, initialState);
    const [hasVerifiedToken, setHasVerifiedToken] = React.useState(false);

    const params = new URLSearchParams(window.location.search);
    const token = useMemo(() => params.get('token') || getAuthTokenFromCookie(), [params]);
    const match = window.location.search.match(/[?&]redirect_uri=([^&]+)/);
    const redirectUri = match ? match[1] : '';

    // Mémorisation de verifyToken avec useCallback
    const verifyToken = useCallback(async () => {
        if (!token) return;
        try {
            dispatchAuth({ type: 'START_AUTH' });
            const userConnected = await handleSharedLinkLogin(token);
            dispatchAuth({
                type: 'SUCCESS_AUTH',
                user: userConnected,
                token,
                authMode: 'token',
            });
            setAuthTokenCookie(token);
            if (redirectUri) {
                const url = rewriteRedirectUri(redirectUri, ['key']);
                window.location.replace(url);
            }
        } catch (err: any) {
            dispatchAuth({ type: 'FAIL_AUTH', error: err });
            clearAuthTokenCookie();
        } finally {
            setHasVerifiedToken(true);
        }
    }, [token]);

    const logout = useCallback(async () => {
        clearAuthTokenCookie();
        dispatchAuth({
            type: 'FAIL_AUTH',
        });
    }, [token]);

    // LOGIN WITH AUTH0
    useEffect(() => {
        const fetchClaims = async () => {
            const claims = await getIdTokenClaims();
            return claims;
        };

        if (isAuthenticated) {
            fetchClaims()
                .then((c) => {
                    if (c?.__raw) {
                        dispatch(setTokenUseCase(c.__raw));
                    }
                })
                .catch(() => clearSurveyCookie(user?.country));
        }
    }, [dispatch, isAuthenticated, getIdTokenClaims, user?.country]);

    // LOGIN WITH TOKEN
    useEffect(() => {
        if (token && !state.isAuthenticated && !state.loading && !hasVerifiedToken) verifyToken();
    }, [token, state.isAuthenticated, state.loading, hasVerifiedToken, verifyToken]);

    // GET USER INFO
    useEffect(() => {
        if (tokenStatus === 'succeeded') dispatch(getUserInfoUseCase());
    }, [dispatch, tokenStatus]);

    useEffect(() => {
        const interval = setInterval(() => setTime(Date.now()), userFeedsRefreshDelayInSeconds);
        return () => {
            clearInterval(interval);
        };
    }, [dispatch, time]);

    const searchParams = new URLSearchParams(document.location.search);
    const urlLanguage = searchParams.get('language');

    // GET USER FEED + STATS | AUTHMODE = AUTH0
    useEffect(() => {
        if (state?.authMode === 'auth0' && tokenStatus === 'succeeded' && user.provider === 'AZ') {
            dispatch(getUserFeedsUseCase());
            dispatch(getUserStatsUseCase());
        }
    }, [dispatch, time, state?.authMode, tokenStatus, user]);

    useEffect(() => {
        if (state.isAuthenticated && state.token) dispatch(setTokenUseCase(state.token));
    }, [state.isAuthenticated, state.token, dispatch]);

    if (state?.authMode === 'auth0' && auth0Error) return <ErrorPage />;
    if (!user || (state?.authMode === 'token' && state?.loading)) return <AuthSpinner />;

    return (
        <HybridAuthContext.Provider value={{ ...state, redirectUri, verifyToken, logout }}>
            <HelmetComponent language={user.language} />
            <TranslateProvider
                projectName="office-app"
                stage={process.env.REACT_APP_ENV === 'prod' ? 'prod' : 'dev'}
                language={user.language || urlLanguage}
            >
                {children}
            </TranslateProvider>
        </HybridAuthContext.Provider>
    );
};
